import { css } from "@emotion/css";
import type { PageSideNavItem } from "@octopusdeploy/design-system-components";
import { ExternalLink, Level2OuterPageLayout } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import React, { useMemo } from "react";
import ArgoCDAvatar from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDAvatar.svg";
import { ArgoCDFeedbackCallout } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDFeedbackCallout";
import { ArgoCDInstanceLinks } from "~/areas/infrastructure/components/ArgoCDInstances/argoCDInstanceLinks";
import { ArgoCDGatewayHealthStatusIcon } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthStatusIcon";
import { useArgoCDIntegration } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDIntegration";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
type ArgoCDInstanceLayoutProps = {
    instanceId: string;
    children: (gateway: ArgoCDGatewayResource) => ReactNode;
};
const styles = {
    connectivityContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[6], //consistent with Machine connectivity nav link space
        alignItems: "center",
    }),
    webUILink: css({
        //The description has a paddingLeft of 0.5rem, but the nav items
        //have 0.75rem
        paddingLeft: space[4],
    }),
};
export const ArgoCDInstanceLayout = (props: ArgoCDInstanceLayoutProps) => {
    const { instanceId, children } = props;
    const { result: gateway, isLoading, error } = useInlineStatusQuery((repo) => repo.ArgoCDGateways.get(instanceId), [instanceId], "Loading ArgoCDGateway");
    const argoCDIntegrationEnabled = useArgoCDIntegration();
    const createSideNavLink = useCreatePageSideNavLink();
    const navLinks: Array<PageSideNavItem> = useMemo(() => {
        if (!gateway) {
            return [];
        }
        const connectivityHeader = (<span className={styles.connectivityContainer}>
                <span>Gateway Connectivity</span>
                <ArgoCDGatewayHealthStatusIcon healthStatus={gateway.HealthStatus} title={gateway.HealthStatus} iconSize={"small"}/>
            </span>);
        return [
            createSideNavLink("Settings", links.argoCDInstanceSettingsPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
            createSideNavLink("Applications", links.argoCDInstanceApplicationsPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
            createSideNavLink(connectivityHeader, links.argoCDInstanceConnectivityPage.generateUrl({ spaceId: gateway.SpaceId, instanceId: gateway.Id })),
        ];
    }, [createSideNavLink, gateway]);
    const linkDescription = useMemo(() => {
        if (!gateway || !gateway.WebUIUri) {
            return;
        }
        const links = new ArgoCDInstanceLinks(gateway.WebUIUri);
        return (<div className={styles.webUILink}>
                <ExternalLink href={links.root()}>Argo CD Web UI</ExternalLink>
            </div>);
    }, [gateway]);
    if (!argoCDIntegrationEnabled) {
        return <RedirectAs404 />;
    }
    //if we are loading or have an error, this does both!
    if (isLoading || error) {
        const errors = error !== null ? getErrorsFromError(error) : undefined;
        return <SkeletonLoadingLayout errors={errors}/>;
    }
    //we didn't load anything but didn't have an error :shrug:
    // noinspection PointlessBooleanExpressionJS
    if (gateway === null) {
        return <RedirectAs404 />;
    }
    const rootLink = links.argoCDInstancesPage.generateUrl({ spaceId: gateway.SpaceId });
    return (<main>
            <Level2OuterPageLayout navItems={navLinks} header={{
            breadcrumbs: [{ label: "Argo CD Instances", pageUrl: rootLink }],
            title: gateway.Name,
            logo: { href: ArgoCDAvatar, accessibleName: "Argo CD logo" },
        }} description={linkDescription} callout={<ArgoCDFeedbackCallout />}>
                {children(gateway)}
            </Level2OuterPageLayout>
        </main>);
};
